/**
 * Cross-provider subscriptions read console (#1701, #1677 Phase B).
 *
 * Backend: `GET /v1/admin/subscriptions` over `subscriptionMirrors` — stripe
 * and supapass today (#1704), apple and google on the same endpoint later.
 *
 * ## Billing state, not access state
 *
 * Every column here reports what a PROVIDER says about billing. None of it is
 * the authorization answer: entitlements are granted from `/externalSubscriptions`
 * and the Stripe event path, and the mirror is deliberately not in that path at
 * all. An `active` row is therefore not a promise that the user can do
 * anything, and this page says so on its face rather than leaving an admin to
 * infer it.
 *
 * ## Filters are one axis at a time, by construction
 *
 * The API accepts at most one of status / normalizedStatus / productId / a user
 * filter, plus `provider` alongside any of those except `productId`; anything
 * else is a 400. Rather than let an admin discover that as an error, the axis is
 * a single selector and the provider control disables itself where it does not
 * compose.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useAdminSubscriptionsList,
  type ListParams,
} from '../../hooks/useAdminSubscriptions';
import { useAdminEntitlementsList } from '../../hooks/useAdminEntitlementsList';
import {
  resolveCatalogCoverage,
  isActionableGap,
} from '../../lib/utils/catalogCoverage';
import {
  NORMALIZED_STATE_LABELS,
  SUBSCRIPTION_PROVIDERS,
  type NormalizedSubscriptionState,
  type SubscriptionFilterAxis,
} from '../../lib/types/subscriptionMirror';
import {
  CoverageCell,
  NormalizedStateBadge,
} from '../../components/admin/subscriptionDisplay';
import {
  formatIso,
  formatRenewal,
  formatUtc,
} from '../../lib/utils/subscriptionFormat';

const PAGE_SIZE = 100;

/**
 * The uid contract (ADR-017), for the free-text uid filter below.
 *
 * Kept in step with `functions/src/config/uid.ts` by
 * `functions/src/api/__tests__/uid-contract.test.ts`: the frontend cannot
 * import from the functions package, so the test asserts the two agree rather
 * than trusting this copy.
 */
const UID_PATTERN = /^[a-zA-Z0-9_-]{1,128}$/;

/*
 * On cell padding, because it looks arbitrary and is not.
 *
 * Nine columns of real data do not fit a 1120px container at 1440px without
 * something giving way, and measurement on the deployed page says the only
 * content lever is the email in the Subscription cell: renaming headers or
 * stacking the status word save literally nothing, because those columns are
 * not at their min-content width. Letting the email break fits the table and
 * wraps 21 of 28 addresses mid-token — the identifier an admin scans most.
 * Letting the product id break wraps every product NAME instead. Dropping the
 * Product column fits with zero headroom, which the product id would then
 * consume.
 *
 * `px-2` fits exactly, wrapping nothing. It is still data-dependent — a longer
 * address will overflow again — which is what the scroll cue on the container
 * is for: fit in the ordinary case, signal clearly when it cannot.
 */

/**
 * Columns, with the three hints that stop a value being read as a claim it is
 * not. `Unlocks` is the important one: on the user card the same word sits
 * beside a line saying what the user actually has, and on this page it does
 * not, so without the hint a catalog mapping reads as the row's access.
 */
const COLUMNS: { name: string; hint?: string }[] = [
  { name: 'Provider' },
  {
    name: 'Subscription',
    hint: "The email on the provider's record. It can differ from the Heapster account's email.",
  },
  { name: 'Status' },
  { name: 'Product' },
  {
    name: 'Unlocks',
    hint: 'What this product unlocks for whoever holds it — not what this user has. Open their record for that.',
  },
  { name: 'Heapster user' },
  { name: 'Renewal' },
  {
    name: 'Covered until (UTC)',
    hint: 'End of the period the user currently has. If the subscription renews, this date moves forward.',
  },
  { name: 'Updated (UTC)' },
];

/**
 * A timestamp as two deliberate lines rather than one that wraps wherever it
 * runs out of room. Left alone, these broke mid-token — "2026-08-" over
 * "18 20:01" — which reads as a rendering fault; and `whitespace-nowrap` is not
 * the fix, because the extra width puts the last column back off-screen at
 * 1440px.
 */
function StackedTimestamp({ value }: { value: string | null }) {
  const text = formatIso(value);
  if (text === '—') return <span className="text-gray-400">—</span>;
  const [date, time] = text.split(' ');
  return (
    <span className="inline-block whitespace-nowrap">
      {date}
      <span className="block text-xs text-gray-400">{time}</span>
    </span>
  );
}

export function SubscriptionsPage() {
  /**
   * `?orphan=1` arrives from the user card's empty state, which used to tell an
   * admin to come here and set two controls by hand. Seeded into BOTH the
   * controls and the applied query, so the page lands showing the answer rather
   * than showing instructions for getting to it.
   */
  const [searchParams] = useSearchParams();
  const seededOrphan = searchParams.get('orphan') === '1';
  const [axis, setAxis] = useState<SubscriptionFilterAxis>(
    seededOrphan ? 'user' : 'none'
  );
  const [provider, setProvider] = useState('');
  const [normalizedStatus, setNormalizedStatus] = useState('');
  const [status, setStatus] = useState('');
  const [productId, setProductId] = useState('');
  const [uid, setUid] = useState('');
  const [orphanOnly, setOrphanOnly] = useState(seededOrphan);

  /**
   * Whether what is typed in the uid box cannot be a uid at all (ADR-017).
   *
   * Advisory only — it renders a hint, and deliberately does NOT block Apply.
   * The server does not pattern-match this filter (a uid there is compared, not
   * used to build a key), so sending an email simply returns an empty page as
   * it always has. Blocking would be this page inventing a restriction the API
   * does not impose.
   *
   * It earns its place because pasting an email here is not a hypothetical
   * slip: it is the documented reason the separate "Email contains" filter
   * exists, and an empty result gives an admin no clue which box they wanted.
   *
   * Scoped to the user axis because that is the only axis whose `uid` is read
   * (`buildParams`) and the only one that renders the hint.
   */
  const uidMalformed =
    axis === 'user' &&
    !orphanOnly &&
    uid.trim() !== '' &&
    !UID_PATTERN.test(uid.trim());
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  /**
   * Client-side, over rows already fetched — deliberately not a server filter.
   * The API's user axis takes a uid, and an admin arrives holding an email.
   * A uid filter would need a backend query, an OpenAPI change and an index;
   * this needs none of them, and says on its face that it only sees what is
   * loaded, exactly as `unmappedOnly` does.
   */
  const [emailContains, setEmailContains] = useState('');

  // `provider` composes with every axis except `productId` (a product id is
  // already provider-specific, so the API declines the redundant pairing).
  const providerAllowed = axis !== 'productId';

  /**
   * Build the request from the current controls.
   *
   * Only ONE axis is ever populated, and a blank control contributes nothing at
   * all — an empty value must not reach the query string, because `provider`
   * and `normalizedStatus` are enums under request validation and `?provider=`
   * is a 400 before the handler runs.
   */
  const buildParams = (): ListParams => {
    const params: ListParams = { limit: PAGE_SIZE };
    if (providerAllowed && provider) params.provider = provider;
    switch (axis) {
      case 'normalizedStatus':
        if (normalizedStatus) params.normalizedStatus = normalizedStatus;
        break;
      case 'status':
        if (status.trim()) params.status = status.trim();
        break;
      case 'productId':
        if (productId.trim()) params.productId = productId.trim();
        break;
      case 'user':
        // uid and orphan are mutually exclusive server-side (400 if both), so
        // the checkbox takes precedence structurally rather than by validation.
        if (orphanOnly) params.orphan = true;
        else if (uid.trim()) params.uid = uid.trim();
        break;
      case 'none':
        break;
    }
    return params;
  };

  /**
   * Applied on submit, not on change, so the request changes only when the
   * admin says so.
   *
   * (An earlier comment here claimed object identity mattered for the React
   * Query key. It does not — TanStack v5 hashes keys structurally, so a fresh
   * but equal object resolves to the same cache entry. The apply-on-submit
   * behaviour is the whole reason.)
   */
  const [queryParams, setQueryParams] = useState<ListParams>(
    seededOrphan ? { limit: PAGE_SIZE, orphan: true } : { limit: PAGE_SIZE }
  );

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    dataUpdatedAt,
  } = useAdminSubscriptionsList(queryParams);

  const {
    data: catalog,
    isLoading: catalogLoading,
    error: catalogError,
  } = useAdminEntitlementsList();
  const externalBundles = catalog?.externalBundles;
  const stripeBundles = catalog?.bundles;

  const rows = useMemo(
    () => (data?.pages ?? []).flatMap(page => page.subscriptions),
    [data]
  );

  const decorated = useMemo(
    () =>
      rows.map(row => {
        const coverage = resolveCatalogCoverage(
          row,
          externalBundles,
          stripeBundles
        );
        return { row, coverage, actionable: isActionableGap(row, coverage) };
      }),
    [rows, externalBundles, stripeBundles]
  );

  const emailNeedle = emailContains.trim().toLowerCase();
  const visible = decorated.filter(d => {
    if (unmappedOnly && d.coverage.state !== 'unmapped') return false;
    if (emailNeedle && !(d.row.email ?? '').toLowerCase().includes(emailNeedle))
      return false;
    return true;
  });

  const actionableCount = decorated.filter(d => d.actionable).length;

  /**
   * Load every remaining page.
   *
   * The catalog gap count below can only ever describe rows that have been
   * fetched, and the collection runs to hundreds of rows against a page size of
   * 100 — so "no red banner on page one" is not an all-clear, and an admin
   * needs a way to make it one. The bound is a runaway guard, not a limit: at
   * 100 per page it covers 5,000 records.
   */
  const [loadingAll, setLoadingAll] = useState(false);
  const loadAll = async () => {
    setLoadingAll(true);
    try {
      let result = await fetchNextPage();
      for (let i = 0; i < 50 && result.hasNextPage; i++) {
        result = await fetchNextPage();
      }
    } finally {
      setLoadingAll(false);
    }
  };

  /**
   * The "nothing found yet, but there is more to look at" panel. Named because
   * it owns the exhaustive-paging control while it is on screen — rendering the
   * same button again in the pager would put two identical controls on the page
   * at once, which is an accessibility problem as much as a visual one.
   */
  const showGapPanel =
    !catalogError && !catalogLoading && actionableCount === 0 && hasNextPage;

  /** Do the controls describe something other than what is on screen? */
  const stable = (p: ListParams) =>
    JSON.stringify(
      Object.fromEntries(
        Object.entries(p).sort(([a], [b]) => a.localeCompare(b))
      )
    );
  const unapplied = stable(buildParams()) !== stable(queryParams);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Subscriptions
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
              What Stripe and Supapass last reported about billing —{' '}
              <strong>not</strong> what a user can actually do. For that, open
              their record.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            {isFetching ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-800 dark:text-red-200">
              {error.message}
            </p>
          </div>
        )}

        {catalogError && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
            <p className="text-sm text-amber-900 dark:text-amber-200">
              <strong>Couldn&apos;t load the catalog</strong>, so no row could
              be checked. A blank column below is not an all-clear. (
              {catalogError.message})
            </p>
          </div>
        )}

        {!catalogError && actionableCount > 0 && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-800 dark:text-red-200">
              <strong>
                {actionableCount} active subscription
                {actionableCount === 1 ? ' is' : 's are'} granting nothing
              </strong>{' '}
              — {actionableCount === 1 ? 'its product' : 'their products'}{' '}
              {actionableCount === 1 ? "isn't" : "aren't"} in the catalog.{' '}
              <button
                type="button"
                onClick={() => {
                  setUnmappedOnly(true);
                  setEmailContains('');
                }}
                className="underline font-medium hover:no-underline"
                title="Shows every unmapped row, including ended ones this count deliberately leaves out."
              >
                {actionableCount === 1 ? 'Show it' : 'Show them'}
              </button>
              , then send the product {actionableCount === 1 ? 'id' : 'ids'} to
              engineering.
              {hasNextPage && (
                <>
                  {' '}
                  Counted across the {rows.length} rows loaded so far — load the
                  rest before trusting this number.
                </>
              )}
            </p>
          </div>
        )}

        {/*
          Silence is the dangerous state here. With more pages unloaded, "no
          banner" means "none in the rows fetched", which is not the question an
          admin is asking, and this page is the compensating control for running
          products uncatalogued with no alerting.
        */}
        {showGapPanel && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md flex flex-wrap items-center gap-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              No catalog problems in the {rows.length} rows loaded — but there
              are more pages, and a problem on a later one isn&apos;t counted
              yet.
            </p>
            <button
              onClick={loadAll}
              disabled={loadingAll}
              className="ml-auto px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 disabled:opacity-50"
            >
              {loadingAll ? 'Loading…' : 'Load every page'}
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-4 text-sm">
            <label className="text-gray-700 dark:text-gray-200">
              <span className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                Filter by
              </span>
              <select
                aria-label="Filter by"
                value={axis}
                onChange={e =>
                  setAxis(e.target.value as SubscriptionFilterAxis)
                }
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              >
                <option value="none">No filter</option>
                <option value="normalizedStatus">Billing state</option>
                <option value="status">Provider status word</option>
                <option value="productId">Product</option>
                <option value="user">User</option>
              </select>
            </label>

            <label className="text-gray-700 dark:text-gray-200">
              <span className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                Provider
              </span>
              <select
                aria-label="Provider"
                value={providerAllowed ? provider : ''}
                disabled={!providerAllowed}
                title={
                  providerAllowed
                    ? undefined
                    : 'A product id is already specific to one provider, so this filter does not compose with it.'
                }
                onChange={e => setProvider(e.target.value)}
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-50"
              >
                <option value="">All providers</option>
                {SUBSCRIPTION_PROVIDERS.map(p => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>

            {axis === 'normalizedStatus' && (
              <label className="text-gray-700 dark:text-gray-200">
                <span className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Billing state
                </span>
                <select
                  aria-label="Billing state"
                  value={normalizedStatus}
                  onChange={e => setNormalizedStatus(e.target.value)}
                  className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                >
                  <option value="">Any</option>
                  {(
                    Object.keys(
                      NORMALIZED_STATE_LABELS
                    ) as NormalizedSubscriptionState[]
                  ).map(state => (
                    <option key={state} value={state}>
                      {NORMALIZED_STATE_LABELS[state]}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {axis === 'status' && (
              <label className="text-gray-700 dark:text-gray-200">
                <span className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Provider&apos;s own word
                </span>
                <input
                  aria-label="Provider status word"
                  value={status}
                  placeholder="e.g. ACTIVE, trialing"
                  onChange={e => setStatus(e.target.value)}
                  className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                />
              </label>
            )}

            {axis === 'productId' && (
              <label className="text-gray-700 dark:text-gray-200">
                <span className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Product id
                </span>
                <input
                  aria-label="Product id"
                  value={productId}
                  placeholder="prod_… or a supapass product name"
                  onChange={e => setProductId(e.target.value)}
                  className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 min-w-[18rem]"
                />
              </label>
            )}

            {axis === 'user' && (
              <>
                <label className="text-gray-700 dark:text-gray-200">
                  <span className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                    Firebase uid
                  </span>
                  <input
                    aria-label="Firebase uid"
                    value={uid}
                    disabled={orphanOnly}
                    onChange={e => setUid(e.target.value)}
                    className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-50"
                  />
                  {uidMalformed && (
                    <span className="block mt-1 text-xs text-amber-700 dark:text-amber-400">
                      Not a uid — an email goes in the “Email contains” box
                      below.
                    </span>
                  )}
                </label>
                <label className="text-gray-700 dark:text-gray-200 pb-1">
                  <input
                    type="checkbox"
                    checked={orphanOnly}
                    onChange={e => setOrphanOnly(e.target.checked)}
                    className="mr-2"
                  />
                  No linked user
                </label>
              </>
            )}

            <button
              onClick={() => setQueryParams(buildParams())}
              className={`ml-auto px-3 py-1.5 text-sm font-medium rounded-md ${
                unapplied
                  ? 'text-white bg-indigo-600 hover:bg-indigo-700'
                  : 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              Apply
            </button>
          </div>

          {unapplied && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Not applied yet — press Apply.
            </p>
          )}

          {axis === 'user' && orphanOnly && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Unlinked Supapass rows are usually just waiting for the person to
              sign up — normal. Unlinked Stripe rows are the ones worth
              checking; use the provider filter.
            </p>
          )}
        </div>

        {/* Results */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {visible.length} of {rows.length} loaded
              {hasNextPage ? ' (more available)' : ''}
              {dataUpdatedAt
                ? ` · last page fetched ${formatUtc(new Date(dataUpdatedAt).toISOString())}`
                : ''}
            </p>
            {/*
              Named as a group. The controls above the table hit the server;
              these two sift what has already been fetched, and the difference
              only showed in a grey caption an admin would not connect to either
              of them.
            */}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Within loaded rows:
            </span>
            <label className="text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={unmappedOnly}
                onChange={e => setUnmappedOnly(e.target.checked)}
                className="mr-2"
              />
              Catalog problems only
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-200">
              <span className="sr-only">Email contains</span>
              <input
                aria-label="Email contains"
                value={emailContains}
                placeholder="Email contains…"
                onChange={e => setEmailContains(e.target.value)}
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
              />
            </label>
            {emailNeedle && hasNextPage && (
              <span className="text-xs text-amber-700 dark:text-amber-300">
                only searching the {rows.length} rows loaded — there are more
              </span>
            )}
          </div>

          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading…
            </div>
          ) : visible.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              <p>No subscriptions match the current filters.</p>
              {emailNeedle && hasNextPage && (
                <div className="mt-2 text-amber-700 dark:text-amber-300">
                  <p>
                    <strong>No match in the rows loaded so far</strong> — there
                    are more to search. This doesn&apos;t mean it isn&apos;t
                    there.
                  </p>
                  <button
                    onClick={loadAll}
                    disabled={loadingAll}
                    className="mt-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 disabled:opacity-50"
                  >
                    {loadingAll
                      ? 'Loading…'
                      : 'Load every page and search again'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto subscriptions-scroll">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    {COLUMNS.map(col => (
                      <th
                        key={col.name}
                        title={col.hint}
                        className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        {col.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {visible.map(({ row, coverage, actionable }) => (
                    <tr
                      key={row.id}
                      className={
                        actionable
                          ? 'bg-red-50/50 dark:bg-red-900/10'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }
                    >
                      <td className="px-2 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {row.provider}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-900 dark:text-white">
                        <span className="break-words">
                          {row.email || (
                            <span className="text-gray-400">no email</span>
                          )}
                        </span>
                        <div className="text-xs text-gray-400 font-mono break-all">
                          {row.providerSubscriptionId}
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <NormalizedStateBadge
                          state={row.normalizedStatus}
                          rawStatus={row.status}
                        />
                        {row.status.toLowerCase() !==
                          NORMALIZED_STATE_LABELS[
                            row.normalizedStatus
                          ]?.toLowerCase() && (
                          <>
                            {' '}
                            <span className="text-xs text-gray-400">
                              {row.status}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {row.productIds.length > 0 ? (
                          <span className="font-mono text-xs">
                            {row.productIds.join(', ')}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-2 py-3 text-sm">
                        <CoverageCell
                          coverage={coverage}
                          actionable={actionable}
                          dense
                        />
                      </td>
                      <td className="px-2 py-3 text-sm">
                        {row.uid ? (
                          row.email ? (
                            <Link
                              to={`/admin?email=${encodeURIComponent(row.email)}`}
                              className="font-mono text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                              title={`${row.uid} — open the user list filtered to ${row.email}. The provider's email may differ from the account's.`}
                            >
                              {row.uid.slice(0, 10)}…
                            </Link>
                          ) : (
                            <span
                              className="font-mono text-xs text-gray-700 dark:text-gray-200"
                              title={row.uid}
                            >
                              {row.uid.slice(0, 10)}…
                            </span>
                          )
                        ) : row.provider === 'stripe' ? (
                          <span
                            className="text-orange-500 dark:text-orange-400"
                            title="A Stripe subscription with no Heapster user. Stripe subscriptions are created at checkout by someone signed in, so this is worth a look."
                          >
                            Not linked
                          </span>
                        ) : (
                          <span
                            className="text-gray-500 dark:text-gray-400"
                            title="Normal for Supapass: we hold the subscription until someone signs up with that email. Not an error."
                          >
                            Awaiting signup
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {formatRenewal(row)}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                        <StackedTimestamp value={row.currentPeriodEnd} />
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                        <StackedTimestamp value={row.updatedAt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasNextPage && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage || loadingAll}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
              {!showGapPanel && (
                <button
                  onClick={loadAll}
                  disabled={isFetchingNextPage || loadingAll}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 disabled:opacity-50"
                >
                  {loadingAll ? 'Loading…' : 'Load every page'}
                </button>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Newest changes first. A record that changes while you page can
                be missed — refresh to start over.
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
